// GtCandidateForms — the Forms tab on a candidate record. Every form this
// person has been sent or answered, newest first, with their answers.
// C1-RECORDFORM: Send a form straight from the record. Forms fold like an
// accordion: all closed to start, one open at a time, chevron to open.
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { GtAnswerList } from './GtFormFields';
import GtSendFormModal from './GtSendFormModal';

function ukDate(v) {
  if (!v) return '';
  const d = new Date(v);
  return isNaN(d) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
const PILL = { answered: 'gtcf-p--green', sent: 'gtcf-p--amber', queued: 'gtcf-p--slate', blocked: 'gtcf-p--rose', failed: 'gtcf-p--rose', link: 'gtcf-p--slate' };

export default function GtCandidateForms({ candidateId, authFetch }) {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await authFetch('/api/candidates/' + candidateId + '/forms');
      setItems(r.forms || []);
    } catch (e) { setError(e.message); }
  }, [authFetch, candidateId]);

  useEffect(() => { load(); }, [load]);

  // The record may be opened by its GTC- reference; sending needs the number.
  async function openSend() {
    if (/^\d+$/.test(String(candidateId))) { setSending(Number(candidateId)); return; }
    try {
      const r = await authFetch('/api/candidates/' + encodeURIComponent(candidateId));
      const id = (r && r.candidate && r.candidate.id) || (r && r.id);
      if (id) setSending(Number(id)); else setSendErr('Could not find this person to send a form to.');
    } catch (e) { setSendErr(e.message); }
  }

  const bar = (
    <div className="gtcf-bar">
      <span>{items && items.length ? items.length + (items.length === 1 ? ' form' : ' forms') : 'Forms'}</span>
      {sendErr && <span className="gtcf-barerr">{sendErr}</span>}
      <button type="button" className="gtc-primary" onClick={openSend}>Send a form</button>
    </div>
  );
  const modal = sending && (
    <GtSendFormModal candidateIds={[sending]} source={{ kind: 'candidates' }} authFetch={authFetch}
      onClose={() => { setSending(false); load(); }} />
  );

  if (error) return <div className="gtr-emptytab">{error}</div>;
  if (!items) return <div className="gtr-emptytab">Loading forms…</div>;
  if (!items.length) {
    return (
      <>
        {bar}
        <div className="gtr2-soon">
          <b>No forms yet</b>
          <p>Forms sent to this person, and anything they answer through a shared form link, appear here with their answers.</p>
        </div>
        {modal}
      </>
    );
  }

  return (
    <div className="gtcf">
      {bar}
      {items.map((f) => {
        const isOpen = openId === f.id;
        const when = f.answered_at
          ? 'Answered ' + ukDate(f.answered_at)
          : f.via === 'link' ? 'Opened the shared link' : f.sent_at ? 'Sent ' + ukDate(f.sent_at) : ukDate(f.created_at);
        return (
          <div key={f.id} className={'gtcf-card' + (isOpen ? ' gtcf-card--open' : '')}>
            <button type="button" className="gtcf-head" onClick={() => setOpenId(isOpen ? null : f.id)} aria-expanded={isOpen}>
              <span className="gtcf-title">{f.title}</span>
              <span className={'gtcf-p ' + (PILL[f.state] || 'gtcf-p--slate')}>{f.state_label}</span>
              <span className="gtcf-when">{when}</span>
              <span className={'gtcf-chev gtcf-chev2' + (isOpen ? ' gtcf-chev2--open' : '')} aria-hidden="true">
                <svg viewBox="0 0 16 16" width="16" height="16"><path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </span>
            </button>
            {isOpen && (
              <div className="gtcf-body">
                <div className="gtcf-meta">
                  {f.via === 'link' ? <span>Through the shared link{f.src ? ' · ' + f.src : ''}</span> : <span>By email{f.source_label ? ' · ' + f.source_label : ''}{f.sent_by_name ? ' · sent by ' + f.sent_by_name : ''}</span>}
                  {f.reminders > 0 && <span>{f.reminders === 1 ? 'Reminded once' : 'Reminded ' + f.reminders + ' times'}</span>}
                  {f.closes_at && <span>Closes {ukDate(f.closes_at)}</span>}
                  <button type="button" className="gtcf-linkbtn" onClick={() => navigate('/forms/' + f.form_id + (f.send_id ? '?scope=' + f.send_id : '?scope=link'))}>All responses</button>
                </div>
                {f.answered_at
                  ? <GtAnswerList questions={f.questions} answers={f.answers} compact />
                  : <div className="gtcf-none">{f.detail ? f.detail : f.opened_at ? 'Opened ' + ukDate(f.opened_at) + ', not answered yet.' : 'Not answered yet.'}</div>}
              </div>
            )}
          </div>
        );
      })}
      {modal}
    </div>
  );
}
